import { cloneDeep, isEmpty } from 'lodash'
import { StreamData } from 'types/streamData'
import { VocSentence } from 'types/vocSentence'
import { ReviewData } from 'types/reviewData'
import StreamSymbol from 'enums/StreamSymbol'
import WidgetType from 'enums/WidgetType'
import { sanitize } from 'utils/sanitizeUtil'
import { setMarkers } from 'utils/rtlTextUtil'
import { replaceMentionedUrls } from 'utils/richMentionedUrlsUtil'
import { resolveReviewDataFromMetadata, addMeasurement } from 'utils/reviewDataUtil'
import { Metadata } from '../../adaptor/metadata'
import sentenceInfosResolver from '../sentence/sentenceInfosResolver'
import resolveHighlightingRelatedFields from '../highlighting/highlightingUtil'
import dataStreamResolver from '../document/dataStreamResolver'

const resolveMeasurement = (metadata: Metadata, index: number, hasSentenceInfo: boolean) => {
  if (
    metadata.getWidgetType() === WidgetType.SOCIAL_WEB_POPULAR_POSTS ||
    metadata.getWidgetType() === WidgetType.SOCIAL_WEB_POPULAR_LINKS ||
    metadata.getWidgetType() === WidgetType.AUDIENCE_POPULAR_LINKS
  ) {
    if (metadata.getSentenceMeasurements() && metadata.getSentenceMeasurements()![index]) {
      return cloneDeep(metadata.getSentenceMeasurements()![index])
    }

    return undefined
  }

  if (hasSentenceInfo) {
    if (metadata.getSentenceMeasurements() && metadata.getSentenceMeasurements()![index]) {
      return cloneDeep(metadata.getSentenceMeasurements()![index])
    }
  } else if (!isEmpty(metadata.getMeasurements()) && metadata.getMeasurements()![index]) {
    return cloneDeep(metadata.getMeasurements()![index])
  }

  return undefined
}

const getUnionTagList = (metaTags: string[], userTags: string[]) => {
  const unionTagList = cloneDeep(metaTags)
  userTags
    .filter((userTag) => !metaTags.includes(userTag))
    .forEach((userTag) => unionTagList.push(userTag))

  return unionTagList
}

const resolveCleanTextRelatedFields = (metadata: Metadata) => {
  let reviewData: ReviewData[] | undefined
  if (
    metadata.getWidgetType() !== WidgetType.SOCIAL_WEB_POPULAR_LINKS ||
    metadata.getWidgetType() !== WidgetType.AUDIENCE_POPULAR_LINKS
  ) {
    reviewData = resolveReviewDataFromMetadata(metadata)
  }

  const internalContents: VocSentence[] = []
  const ellipsisIndices: number[] = []
  let cleanText: string | undefined

  let previousSentenceNumber = 0
  const sentences = metadata.getSentences()
  if (!sentences) {
    return { cleanText: '', ellipsisIndices, internalContents } as StreamData
  }

  sentences!.forEach((sentence, index) => {
    const { sentenceInfos } = sentenceInfosResolver(metadata)
    const hasSentenceInfo = sentenceInfos !== undefined && sentenceInfos[index] !== undefined
    const measurement = resolveMeasurement(metadata, index, hasSentenceInfo)
    const currSentenceNumber = hasSentenceInfo
      ? Number(sentenceInfos[index].sentenceNumber)
      : previousSentenceNumber

    if (index > 0 && hasSentenceInfo && currSentenceNumber - previousSentenceNumber > 1) {
      ellipsisIndices.push(index)
    }

    previousSentenceNumber = currSentenceNumber

    const cleanSentence = sanitize(sentence!).replace(/\u200B/g, '')
    let displaySentence = cleanSentence
    let highlightedSent
    const { highlightedSentences } = resolveHighlightingRelatedFields(metadata)
    if (highlightedSentences) {
      highlightedSent = highlightedSentences![index]
    }

    if (metadata.getHitSentence() && sentence! !== metadata.getHitSentence()) {
      displaySentence = `<span class="peer-sentence">${sentence!}</span>`
      highlightedSent = `<span class="peer-sentence">${sentence!}</span>`
    }

    const { dataStream } = dataStreamResolver(metadata)
    const isInternal = dataStream === StreamSymbol.VOC
    const isLxnx = dataStream === StreamSymbol.LXNX

    if (isInternal && metadata.isVocDisplay()) {
      let vocSentence: any = {
        cleanText: cleanSentence,
        displayText: setMarkers(displaySentence)!
      }

      if (highlightedSent) {
        vocSentence = {
          ...vocSentence,
          highlightedText: setMarkers(
            replaceMentionedUrls(highlightedSent, metadata.getMentionedUrls()!)
          )!
        }
      }

      if (metadata.getSentenceMetaTags() && metadata.getSentenceUserTags()) {
        vocSentence = {
          ...vocSentence,
          tags: getUnionTagList(
            metadata.getSentenceMetaTags()![index] as string[],
            metadata.getSentenceUserTags()![index] as string[]
          )
        }
      }

      if (measurement !== undefined) {
        reviewData = addMeasurement(reviewData!, { ...measurement, ...vocSentence })
      } else {
        internalContents.push(vocSentence)
      }
    } else if (!isLxnx || (isLxnx && index < 5)) {
      cleanText = cleanText ? `${cleanText} ${cleanSentence}` : cleanSentence
    }
  })
  if (reviewData) {
    reviewData = reviewData.filter((data) => data.header.value !== -2)
  }

  return {
    reviewData,
    internalContents,
    ellipsisIndices,
    cleanText
  } as StreamData
}

export default resolveCleanTextRelatedFields
